import { useRef, useState } from "react";

/**
 * 3D tactile access card. Tilts toward the cursor, gold-foil sheen tracks pointer.
 */
export default function AccessCard() {
  const ref = useRef<HTMLDivElement>(null);
  const [t, setT] = useState({ rx: 0, ry: 0, mx: 50, my: 50, lifted: false });

  const onMove = (e: React.PointerEvent) => {
    const el = ref.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const px = (e.clientX - r.left) / r.width;
    const py = (e.clientY - r.top) / r.height;
    const ry = (px - 0.5) * 22;
    const rx = -(py - 0.5) * 22;
    setT({ rx, ry, mx: px * 100, my: py * 100, lifted: true });
  };
  const onLeave = () => setT({ rx: 0, ry: 0, mx: 50, my: 50, lifted: false });

  return (
    <div className="relative w-full" style={{ perspective: "1400px" }}>
      <div
        ref={ref}
        onPointerMove={onMove}
        onPointerLeave={onLeave}
        className="relative w-full mx-auto select-none"
        style={{
          aspectRatio: "1.586 / 1",
          maxWidth: 480,
          borderRadius: 16,
          background:
            "linear-gradient(135deg, #1a1814 0%, #0a0a08 45%, #050504 100%)",
          border: "1px solid rgba(201,168,107,0.28)",
          boxShadow: t.lifted
            ? "0 40px 80px -20px rgba(0,0,0,0.95), 0 80px 160px -40px rgba(0,0,0,0.7), 0 0 0 1px rgba(229,200,131,0.4), 0 0 80px -10px rgba(201,168,107,0.4)"
            : "0 30px 60px -20px rgba(0,0,0,0.9), 0 60px 120px -40px rgba(0,0,0,0.7), 0 0 0 1px rgba(201,168,107,0.18)",
          transform: `rotateX(${t.rx}deg) rotateY(${t.ry}deg) translateZ(0)`,
          transition: "transform 0.4s cubic-bezier(0.16,1,0.3,1), box-shadow 0.6s cubic-bezier(0.16,1,0.3,1)",
          transformStyle: "preserve-3d",
        }}
      >
        {/* Inner edge */}
        <div
          className="absolute pointer-events-none"
          style={{
            inset: 8,
            borderRadius: 12,
            border: "1px solid rgba(201,168,107,0.22)",
          }}
        />

        {/* Gold-foil sheen tracking cursor */}
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            borderRadius: 16,
            background: `radial-gradient(circle at ${t.mx}% ${t.my}%, rgba(229,200,131,0.35) 0%, rgba(201,168,107,0.12) 25%, transparent 55%)`,
            mixBlendMode: "screen",
            opacity: t.lifted ? 1 : 0.55,
            transition: "opacity 0.4s ease-out",
          }}
        />

        {/* Fine matte grain */}
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            borderRadius: 16,
            backgroundImage:
              "url(\"data:image/svg+xml;utf8,<svg viewBox='0 0 120 120' xmlns='http://www.w3.org/2000/svg'><filter id='g'><feTurbulence type='fractalNoise' baseFrequency='1.4' numOctaves='2'/><feColorMatrix values='0 0 0 0 1  0 0 0 0 1  0 0 0 0 1  0 0 0 0.18 0'/></filter><rect width='100%25' height='100%25' filter='url(%23g)'/></svg>\")",
            backgroundSize: "120px 120px",
            opacity: 0.45,
            mixBlendMode: "overlay",
          }}
        />

        {/* Content layer */}
        <div
          className="absolute inset-0 flex flex-col justify-between"
          style={{
            padding: "clamp(18px, 4%, 28px)",
            transform: "translateZ(40px)",
          }}
        >
          {/* Top row */}
          <div className="flex items-start justify-between">
            <span
              style={{
                fontFamily: "'JetBrains Mono', monospace",
                fontSize: 9.5,
                letterSpacing: "0.28em",
                textTransform: "uppercase",
                color: "#8c764a",
              }}
            >
              Closer Academy
            </span>
            <span
              style={{
                fontFamily: "'JetBrains Mono', monospace",
                fontSize: 9.5,
                letterSpacing: "0.28em",
                textTransform: "uppercase",
                color: "#5a544c",
              }}
            >
              MEM · 001
            </span>
          </div>

          {/* Chip + monogram */}
          <div className="flex items-end justify-between gap-4">
            <div
              aria-hidden
              style={{
                width: "clamp(38px, 9%, 52px)",
                height: "clamp(28px, 7%, 38px)",
                borderRadius: 5,
                background:
                  "linear-gradient(135deg, #d9bb7d 0%, #8c764a 40%, #e5c883 70%, #6e5a35 100%)",
                boxShadow:
                  "inset 0 1px 0 rgba(255,255,255,0.3), inset 0 -1px 0 rgba(0,0,0,0.4), 0 2px 6px rgba(0,0,0,0.5)",
                position: "relative",
              }}
            >
              <div
                className="absolute inset-1"
                style={{
                  borderRadius: 3,
                  background:
                    "repeating-linear-gradient(0deg, rgba(0,0,0,0.25) 0 2px, transparent 2px 5px), repeating-linear-gradient(90deg, rgba(0,0,0,0.25) 0 2px, transparent 2px 5px)",
                  opacity: 0.6,
                }}
              />
            </div>

            <span
              style={{
                fontFamily: "'Fraunces', serif",
                fontVariationSettings: "'opsz' 144, 'SOFT' 50",
                fontStyle: "italic",
                fontWeight: 400,
                fontSize: "clamp(3rem, 7vw, 4.5rem)",
                lineHeight: 0.85,
                letterSpacing: "-0.04em",
                background:
                  "linear-gradient(135deg, #f4ede2 0%, #e5c883 50%, #c9a86b 100%)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                backgroundClip: "text",
              }}
            >
              CA
            </span>
          </div>

          {/* Bottom row */}
          <div className="flex items-end justify-between">
            <div className="flex flex-col gap-1">
              <span
                style={{
                  fontFamily: "'JetBrains Mono', monospace",
                  fontSize: 8.5,
                  letterSpacing: "0.28em",
                  textTransform: "uppercase",
                  color: "#5a544c",
                }}
              >
                Holder
              </span>
              <span
                style={{
                  fontFamily: "'Fraunces', serif",
                  fontVariationSettings: "'opsz' 96, 'SOFT' 30",
                  fontSize: 15,
                  color: "#f4ede2",
                  letterSpacing: "0.02em",
                }}
              >
                The Operator
              </span>
            </div>
            <div className="flex flex-col items-end gap-1">
              <span
                style={{
                  fontFamily: "'JetBrains Mono', monospace",
                  fontSize: 8.5,
                  letterSpacing: "0.28em",
                  textTransform: "uppercase",
                  color: "#5a544c",
                }}
              >
                Tier
              </span>
              <span
                style={{
                  fontFamily: "'JetBrains Mono', monospace",
                  fontSize: 11,
                  letterSpacing: "0.22em",
                  textTransform: "uppercase",
                  color: "#e5c883",
                }}
              >
                Private
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Card hint → points to VSL below */}
      <div className="mt-6 flex justify-center">
        <a
          href="#vsl"
          className="group inline-flex items-center gap-2 transition-colors"
          style={{
            fontFamily: "'JetBrains Mono', monospace",
            fontSize: 11,
            letterSpacing: "0.22em",
            textTransform: "uppercase",
            color: "#8a8378",
          }}
        >
          <span>Watch the 7-min breakdown</span>
          <span
            aria-hidden
            className="transition-transform group-hover:translate-y-0.5"
            style={{ color: "#c9a86b" }}
          >
            ↓
          </span>
        </a>
      </div>
    </div>
  );
}
